import React, { useState } from "react";
import SkeletonBase from "newui/components/Skeleton";
import { useRfxData } from "newui/features/Rfx/data-provider/RfxDataProvider";
import { useAwards } from "../../../hooks/useAwards";
import { useEvaluationCompletion } from "../../../hooks/useEvaluationCompletion";
import type { AwardRow, CompletionState } from "../../../types/award.type";
import { SupplierResultsTable } from "./components/SupplierResultsTable";
import { AwardDrawer } from "./components/AwardDrawer";
import { NotAwardedDrawer } from "./components/NotAwardedDrawer";
import { ReportPanel } from "./components/ReportPanel";

interface CompletionBannerProps {
	completion: CompletionState | undefined;
}

const CompletionBanner: React.FC<CompletionBannerProps> = ({ completion }) => {
	// Hidden until completion data is loaded and the evaluation is complete.
	if (!completion || !completion.complete) {
		return null;
	}

	return (
		<div
			role="status"
			data-testid="completion-banner"
			className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3 mb-4"
		>
			<span className="text-sm font-semibold text-green-800">
				Evaluation Complete
			</span>
			<span className="text-sm text-green-700">
				{completion.awarded} of {completion.total} suppliers decided
			</span>
		</div>
	);
};

interface FinalReportAndAwardStepProps {
	evalId?: number;
	isRfxLead?: boolean;
	rfxId?: number;
	rfxSubmissionId?: number;
}

export const FinalReportAndAwardStep: React.FC<FinalReportAndAwardStepProps> = ({
	rfxId,
	evalId,
	isRfxLead = false,
}) => {
	const rfxContext = useRfxData(true);
	const rfx = rfxContext?.rfxData ?? null;
	const { awardsData, isLoading } = useAwards(rfxId, evalId);
	const { completion: completionData } = useEvaluationCompletion(rfxId, evalId);

	// Drawer state is local (not global store): which supplier is being awarded
	// and which supplier(s) are being marked Not Awarded.
	const [awardTarget, setAwardTarget] = useState<AwardRow | null>(null);
	const [notAwardedTargets, setNotAwardedTargets] = useState<AwardRow[]>([]);

	// The dedicated completion endpoint (T003) is the source of truth once live;
	// until then fall back to the completion summary on the awards payload.
	const completion = completionData ?? awardsData?.completion;

	if (isLoading) {
		return (
			<div className="space-y-3" data-testid="final-report-award-step-loading">
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
			</div>
		);
	}

	const awards: AwardRow[] = awardsData?.awards ?? [];
	const awardedSuppliers = awards.filter(
		(row) => row.award_status === "awarded"
	);

	const handleAward = (row: AwardRow): void => {
		setAwardTarget(row);
	};

	const handleMarkNotAwarded = (rows: AwardRow[]): void => {
		setNotAwardedTargets(rows);
	};

	return (
		<div data-testid="final-report-award-step">
			<CompletionBanner completion={completion} />

			<SupplierResultsTable
				readOnly={!isRfxLead}
				awards={awards}
				rfxId={rfxId}
				evalId={evalId}
				onAward={handleAward}
				onMarkNotAwarded={handleMarkNotAwarded}
			/>

			{/* `key` remounts each drawer per supplier/selection so Formik
			    re-initialises — a typed reason never carries over to a different
			    supplier when the drawer is reopened. */}
			<AwardDrawer
				key={`award-${awardTarget?.rfx_invite_id ?? "none"}`}
				isOpen={!!awardTarget}
				supplier={awardTarget}
				rfx={rfx}
				rfxId={rfxId}
				evalId={evalId}
				onClose={() => setAwardTarget(null)}
			/>

			<NotAwardedDrawer
				key={`not-awarded-${notAwardedTargets
					.map((t) => t.rfx_invite_id)
					.join("-") || "none"}`}
				isOpen={notAwardedTargets.length > 0}
				suppliers={notAwardedTargets}
				rfx={rfx}
				rfxId={rfxId}
				evalId={evalId}
				onClose={() => setNotAwardedTargets([])}
			/>

			<div className="mt-8">
				<ReportPanel
					rfxId={rfxId}
					evalId={evalId}
					awardedSuppliers={awardedSuppliers}
				/>
			</div>
		</div>
	);
};
